import { reportingPlatform } from '@/lib/reporting-platforms';
import type {
  WeekSelectorPresetRangeKey,
  WeekSelectorValue,
  PipelinePeriodScopeParams,
} from '@/components/shared/WeekSelector';
import {
  customRangeSelection,
  localCalendarDateKey,
  rangeSelectionFromJobs,
} from '@/components/shared/WeekSelector';
import type { PromptGroup } from '@/types/database-schema';
import { topicOrigin } from '@/utils/topicOptions';
import {
  parsePromptTopicFilter,
  resolvePromptTopicFilter,
  topicFilterBase,
  type PromptTopicFilter,
} from '@/utils/promptTopicFilter';
import {
  groupJobsByMonth,
  groupJobsByWeek,
  type PeriodGranularity,
  type WeekJobRecord,
} from '@/utils/weekJobBuckets';

type SearchParamsLike = Pick<URLSearchParams, 'get'>;

const PROMPT_GROUP_PARAM_KEYS = ['prompt_group', 'prompt_group_id'] as const;

type PromptScopeSource = 'group' | 'legacy_prompt_ids' | 'filter' | 'none';

export interface ParsedBrandWeekScope {
  rangeKey: 'all_time' | WeekSelectorPresetRangeKey | 'custom' | '';
  startDate: string;
  endDate: string;
  weekKey: string;
  jobId: string;
  jobIds: string[];
  periodGrain: PeriodGranularity | null;
  periodKey: string;
}

export interface ResolvedBrandPromptScope {
  promptGroupId: string;
  promptGroupOrigin?: 'shared';
  promptSubtopicId?: string;
  promptIds: string[];
  topicFilter?: PromptTopicFilter;
  topicFilterInvalid?: boolean;
  source: PromptScopeSource;
}

export interface ResolvedBrandScope {
  week: ParsedBrandWeekScope;
  engine?: string;
  promptGroupId: string;
  promptGroupOrigin?: 'shared';
  promptSubtopicId?: string;
  promptIds: string[];
  promptScopeSource: PromptScopeSource;
  topicFilter?: PromptTopicFilter;
  topicFilterInvalid?: boolean;
}

export interface BuildBrandScopeSearchParamsInput {
  weekSelection: WeekSelectorValue;
  periodGranularity?: PeriodGranularity;
  engine?: string | null;
  promptGroupId?: string | null;
  promptGroupOrigin?: 'local' | 'shared';
  promptSubtopicId?: string | null;
  promptIds?: string[];
  topicFilter?: PromptTopicFilter | null;
  promptId?: string;
}

export function parsePromptIdsParam(raw: string | null): string[] {
  if (!raw) return [];
  return Array.from(
    new Set(
      raw
        .split(',')
        .map(value => value.trim())
        .filter(Boolean)
    )
  ).sort();
}

function parseJobIdsParam(raw: string | null): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const values: string[] = [];
  for (const id of raw
    .split(',')
    .map(value => value.trim())
    .filter(Boolean)) {
    if (!seen.has(id)) {
      seen.add(id);
      values.push(id);
    }
  }
  return values;
}

function parsePromptGroupId(searchParams: SearchParamsLike): string {
  for (const key of PROMPT_GROUP_PARAM_KEYS) {
    const value = searchParams.get(key);
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return '';
}

export function parseEngineParam(raw: string | null): string | undefined {
  const value = reportingPlatform(raw || '');
  return value.length > 0 ? value : undefined;
}

function isValidCalendarDateKey(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

export function parseBrandWeekScope(searchParams: SearchParamsLike): ParsedBrandWeekScope {
  const periodGrainRaw = (searchParams.get('period_grain') || '').trim().toLowerCase();
  const periodGrain: PeriodGranularity | null =
    periodGrainRaw === 'week' || periodGrainRaw === 'month' ? periodGrainRaw : null;
  const rangeRaw = (searchParams.get('range') || '').trim().toLowerCase();
  const startDate = (searchParams.get('start_date') || '').trim();
  const endDate = (searchParams.get('end_date') || '').trim();
  const hasValidCustomRange =
    isValidCalendarDateKey(startDate) && isValidCalendarDateKey(endDate) && startDate <= endDate;
  const rangeKey =
    rangeRaw === 'all_time' ||
    rangeRaw === 'last_7_days' ||
    rangeRaw === 'last_30_days' ||
    rangeRaw === 'last_3_months' ||
    rangeRaw === 'last_6_months' ||
    rangeRaw === 'last_year' ||
    (rangeRaw === 'custom' && hasValidCustomRange)
      ? rangeRaw
      : '';
  return {
    rangeKey,
    startDate,
    endDate,
    weekKey: (searchParams.get('week') || '').trim(),
    jobId: (searchParams.get('job_id') || '').trim(),
    jobIds: parseJobIdsParam(searchParams.get('job_ids')),
    periodGrain,
    periodKey: (searchParams.get('period_key') || '').trim(),
  };
}

function areStringArraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
}

function normalizeGroupPromptIds(group: PromptGroup): string[] {
  return Array.from(
    new Set((group.prompt_ids || []).map(value => String(value || '').trim()).filter(Boolean))
  ).sort();
}

export function resolveBrandPromptScope(
  searchParams: SearchParamsLike,
  promptGroups: PromptGroup[],
  promptInventoryIds?: string[]
): ResolvedBrandPromptScope {
  const rawFilter = searchParams.get('topic_filter');
  if (rawFilter) {
    const topicFilter = parsePromptTopicFilter(rawFilter);
    if (!topicFilter)
      return { promptGroupId: '', promptIds: [], source: 'filter', topicFilterInvalid: true };
    const result = resolvePromptTopicFilter(topicFilter, promptGroups, promptInventoryIds);
    const base = topicFilterBase(topicFilter);
    const group = promptGroups.find(group => group.id === base?.topicId);
    return {
      promptGroupId: base?.topicId || '',
      promptGroupOrigin: topicOrigin(group),
      ...(base?.subtopicId ? { promptSubtopicId: base.subtopicId } : {}),
      promptIds: result.promptIds,
      topicFilter,
      ...(!result.valid || !result.promptIds.length ? { topicFilterInvalid: true } : {}),
      source: 'filter',
    };
  }
  const promptGroupId = parsePromptGroupId(searchParams);
  const promptSubtopicId = (searchParams.get('prompt_subtopic_id') || '').trim() || undefined;
  const promptGroupOrigin =
    searchParams.get('prompt_group_origin') === 'shared' ? 'shared' : undefined;
  const explicitGroup = promptGroups.find(
    group => group.id === promptGroupId && topicOrigin(group) === promptGroupOrigin
  );

  if (promptGroupId && !explicitGroup) {
    return {
      promptGroupId,
      promptGroupOrigin,
      ...(promptSubtopicId ? { promptSubtopicId } : {}),
      promptIds: [],
      source: 'group',
    };
  }
  if (explicitGroup) {
    const members = promptSubtopicId
      ? explicitGroup.subtopics?.find(child => child.id === promptSubtopicId)?.prompt_ids || []
      : explicitGroup.prompt_ids;
    return {
      promptGroupId: explicitGroup.id,
      promptGroupOrigin: topicOrigin(explicitGroup),
      ...(promptSubtopicId ? { promptSubtopicId } : {}),
      promptIds: searchParams.get('prompt_ids')
        ? parsePromptIdsParam(searchParams.get('prompt_ids')).filter(id => members.includes(id))
        : [...new Set(members)].sort(),
      source: 'group',
    };
  }

  if (promptSubtopicId)
    return { promptGroupId: '', promptSubtopicId, promptIds: [], source: 'group' };

  const legacyPromptIds = parsePromptIdsParam(searchParams.get('prompt_ids'));
  if (legacyPromptIds.length === 0) {
    return {
      promptGroupId: '',
      promptIds: [],
      source: 'none',
    };
  }

  const matchingGroup = promptGroups.find(
    group =>
      !topicOrigin(group) && areStringArraysEqual(legacyPromptIds, normalizeGroupPromptIds(group))
  );
  if (matchingGroup) {
    return {
      promptGroupId: matchingGroup.id,
      promptIds: normalizeGroupPromptIds(matchingGroup),
      source: 'legacy_prompt_ids',
    };
  }

  return {
    promptGroupId: '',
    promptIds: legacyPromptIds,
    source: 'legacy_prompt_ids',
  };
}

export function resolveBrandScope(
  searchParams: SearchParamsLike,
  promptGroups: PromptGroup[],
  promptInventoryIds?: string[]
): ResolvedBrandScope {
  const promptScope = resolveBrandPromptScope(searchParams, promptGroups, promptInventoryIds);
  return {
    week: parseBrandWeekScope(searchParams),
    engine: parseEngineParam(searchParams.get('engine')),
    promptGroupId: promptScope.promptGroupId,
    promptGroupOrigin: promptScope.promptGroupOrigin,
    ...(promptScope.promptSubtopicId ? { promptSubtopicId: promptScope.promptSubtopicId } : {}),
    promptIds: promptScope.promptIds,
    promptScopeSource: promptScope.source,
    ...(promptScope.topicFilter ? { topicFilter: promptScope.topicFilter } : {}),
    ...(promptScope.topicFilterInvalid ? { topicFilterInvalid: true } : {}),
  };
}

export function resolveWeekSelectionFromParsedScope(
  parsedScope: ParsedBrandWeekScope,
  jobs: readonly WeekJobRecord[],
  end: Date = new Date()
): WeekSelectorValue {
  if (parsedScope.rangeKey === 'all_time') return 'all';
  if (parsedScope.rangeKey === 'custom') {
    return customRangeSelection(parsedScope.startDate, parsedScope.endDate);
  }
  if (parsedScope.rangeKey) return rangeSelectionFromJobs(jobs, parsedScope.rangeKey, end);

  const hasExplicitWeek = parsedScope.weekKey.length > 0;
  const hasExplicitPeriod = parsedScope.periodGrain !== null && parsedScope.periodKey.length > 0;
  const hasExplicitJobScope = parsedScope.jobId.length > 0 || parsedScope.jobIds.length > 0;
  if (!hasExplicitWeek && !hasExplicitPeriod && !hasExplicitJobScope) {
    return 'all';
  }

  if (hasExplicitPeriod) {
    if (parsedScope.periodGrain === 'month') {
      const monthBuckets = groupJobsByMonth(jobs);
      const monthBucket = monthBuckets.find(month => month.monthKey === parsedScope.periodKey);
      if (monthBucket) {
        if (
          parsedScope.jobIds.length === 0 ||
          parsedScope.jobIds.every(id => monthBucket.jobIds.includes(id))
        ) {
          return {
            grain: 'month',
            monthKey: monthBucket.monthKey,
            jobIds: parsedScope.jobIds.length > 0 ? parsedScope.jobIds : monthBucket.jobIds,
          };
        }
      }
    } else {
      const weekBuckets = groupJobsByWeek(jobs);
      const weekBucket = weekBuckets.find(week => week.weekKey === parsedScope.periodKey);
      if (weekBucket) {
        if (
          parsedScope.jobIds.length === 0 ||
          parsedScope.jobIds.every(id => weekBucket.jobIds.includes(id))
        ) {
          return {
            grain: 'week',
            weekKey: weekBucket.weekKey,
            jobIds: parsedScope.jobIds.length > 0 ? parsedScope.jobIds : weekBucket.jobIds,
          };
        }
      }
    }
  }

  const buckets = groupJobsByWeek(jobs);

  if (parsedScope.weekKey && parsedScope.jobIds.length > 0) {
    const bucket = buckets.find(week => week.weekKey === parsedScope.weekKey);
    if (bucket && parsedScope.jobIds.every(id => bucket.jobIds.includes(id))) {
      return { grain: 'week', weekKey: parsedScope.weekKey, jobIds: parsedScope.jobIds };
    }
  }

  if (parsedScope.jobId) {
    const containingBucket = buckets.find(bucket => bucket.jobIds.includes(parsedScope.jobId));
    if (containingBucket) {
      return {
        grain: 'week',
        weekKey: containingBucket.weekKey,
        jobIds: containingBucket.jobIds,
      };
    }
  }

  return 'all';
}

export function buildBrandScopeSearchParams({
  weekSelection,
  periodGranularity,
  engine,
  promptGroupId,
  promptGroupOrigin,
  promptSubtopicId,
  promptIds,
  topicFilter,
  promptId,
}: BuildBrandScopeSearchParamsInput): URLSearchParams {
  const params = new URLSearchParams();

  if (weekSelection === 'all') {
    params.set('range', 'all_time');
  } else if (weekSelection.grain === 'range') {
    params.set('range', weekSelection.rangeKey);
    if (weekSelection.rangeKey === 'custom') {
      params.set('start_date', localCalendarDateKey(weekSelection.startDate));
      params.set('end_date', localCalendarDateKey(weekSelection.endDate));
    }
  } else if (periodGranularity) {
    params.set('period_grain', periodGranularity);
  }

  if (weekSelection !== 'all' && weekSelection.grain !== 'range') {
    if (weekSelection.grain === 'month') {
      params.set('period_grain', 'month');
      params.set('period_key', weekSelection.monthKey);
    } else {
      params.set('period_grain', 'week');
      params.set('period_key', weekSelection.weekKey);
    }
  }

  const normalizedEngine = parseEngineParam(engine ?? null);
  if (normalizedEngine) {
    params.set('engine', normalizedEngine);
  }

  const normalizedPromptGroupId = (promptGroupId || '').trim();
  const normalizedPromptIds = Array.from(
    new Set((promptIds || []).map(value => String(value || '').trim()).filter(Boolean))
  ).sort();
  if (normalizedPromptGroupId) {
    params.set('prompt_group', normalizedPromptGroupId);
    if (promptGroupOrigin === 'shared') params.set('prompt_group_origin', 'shared');
  }
  if (!topicFilter && normalizedPromptIds.length)
    params.set('prompt_ids', normalizedPromptIds.join(','));
  if (topicFilter) params.set('topic_filter', JSON.stringify(topicFilter));
  if (promptId) params.set('prompt_id', promptId);
  if (promptSubtopicId?.trim()) params.set('prompt_subtopic_id', promptSubtopicId.trim());
  return params;
}

export function copyBrandScopeSearchParams(searchParams: SearchParamsLike): URLSearchParams {
  const parsedWeekScope = parseBrandWeekScope(searchParams);
  const params = new URLSearchParams();
  if (parsedWeekScope.rangeKey) params.set('range', parsedWeekScope.rangeKey);
  if (parsedWeekScope.rangeKey === 'custom') {
    params.set('start_date', parsedWeekScope.startDate);
    params.set('end_date', parsedWeekScope.endDate);
  }
  if (parsedWeekScope.periodGrain) params.set('period_grain', parsedWeekScope.periodGrain);
  if (parsedWeekScope.periodKey) params.set('period_key', parsedWeekScope.periodKey);

  const parsedEngine = parseEngineParam(searchParams.get('engine'));
  if (parsedEngine) params.set('engine', parsedEngine);

  const rawFilter = searchParams.get('topic_filter');
  if (rawFilter) params.set('topic_filter', rawFilter);

  const promptId = (searchParams.get('prompt_id') || '').trim();
  if (promptId) params.set('prompt_id', promptId);

  const promptGroupId = parsePromptGroupId(searchParams);
  const promptSubtopicId = (searchParams.get('prompt_subtopic_id') || '').trim() || undefined;
  if (promptGroupId) {
    params.set('prompt_group', promptGroupId);
    if (searchParams.get('prompt_group_origin') === 'shared')
      params.set('prompt_group_origin', 'shared');
    if (promptSubtopicId) params.set('prompt_subtopic_id', promptSubtopicId);
    const promptIds = parsePromptIdsParam(searchParams.get('prompt_ids'));
    if (!rawFilter && promptIds.length) params.set('prompt_ids', promptIds.join(','));
    return params;
  }

  if (promptSubtopicId) params.set('prompt_subtopic_id', promptSubtopicId);
  const legacyPromptIds = parsePromptIdsParam(searchParams.get('prompt_ids'));
  if (!rawFilter && legacyPromptIds.length > 0) {
    params.set('prompt_ids', legacyPromptIds.join(','));
  }

  return params;
}

export function periodScopeFromParsedBrandScope(
  parsedScope: ParsedBrandWeekScope
): PipelinePeriodScopeParams | null {
  if (!parsedScope.periodGrain || !parsedScope.periodKey) return null;
  return {
    period_grain: parsedScope.periodGrain,
    period_key: parsedScope.periodKey,
  };
}

export function periodSelectionFromWeekSelection(
  selection: WeekSelectorValue
): PipelinePeriodScopeParams | null {
  if (selection === 'all') return null;
  if (selection.grain === 'range') return null;
  if (selection.grain === 'month') {
    return {
      period_grain: 'month',
      period_key: selection.monthKey,
    };
  }
  return {
    period_grain: 'week',
    period_key: selection.weekKey,
  };
}

export function normalizeWeekSelectionForGranularity(
  selection: WeekSelectorValue,
  granularity: PeriodGranularity
): WeekSelectorValue {
  if (selection === 'all') return selection;
  if (selection.grain === 'range') return selection;
  if (granularity === 'month' && selection.grain === 'month') return selection;
  if (granularity === 'week' && (selection.grain === 'week' || !selection.grain)) {
    return {
      grain: 'week',
      weekKey: selection.weekKey,
      jobIds: selection.jobIds,
    };
  }
  return 'all';
}
